import { RouteRecordRaw } from 'vue-router';
import { requireAuth, setDayjsTimezone } from '@/common/router/navigationGuard';
import BaseViewport from '@/common/components/templates/baseViewport/BaseViewport.vue';
import { VIEWPORT_TYPE } from '@/common/components/templates/baseViewport/baseViewport.define';
import { VIEW_MODE } from '@/common/define/common.define';
import { extractAccessibleRoutes } from '@/common/auth/auth.utils';
import { ROLE_PERMISSION_KEY } from '@/common/define/rolePermission.define';
import { type MaxgaugeDatabaseSubMenu } from '@/common/utils';
import { BASE_MENU_VALUE } from '@/common/components/organisms/baseMenu/baseMenu.define';
import {
  requireDomainTargetOrTutorial,
  requireInstances,
  validateDatabaseOverviewPath,
} from '@/common/router/authGuard';

const PREFIX_SQL_SERVER = 'sqlserver_';

const sqlServerMenu: RouteRecordRaw[] = [
  {
    path: '/sqlserver/',
    name: `${PREFIX_SQL_SERVER}Real Time Monitoring`,
    component: BaseViewport,
    children: [
      {
        path: 'singleView',
        name: `${PREFIX_SQL_SERVER}Single View`,
        component: () => import('@/sqlServer/views/singleView/SingleView.vue'),
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
    ],
    beforeEnter: [
      requireAuth,
      () => requireDomainTargetOrTutorial('database'),
      requireInstances,
      setDayjsTimezone,
      validateDatabaseOverviewPath,
    ],
    meta: {
      rolePermission: ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_FEATURED_DASHBOARD,
      invisibleViewMode: [VIEW_MODE.INTERMAX],
      viewport: VIEWPORT_TYPE.REALTIME,
    },
  },
  {
    path: '/sqlserver/',
    name: `${PREFIX_SQL_SERVER}SQL Analysis`,
    component: BaseViewport,
    children: [
      {
        path: 'topNAnalysis',
        name: `${PREFIX_SQL_SERVER}Top N Analysis`,
        component: () => import('@/sqlServer/views/topNAnalysis/TopNAnalysis.vue'),
        beforeEnter: [requireInstances],
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
      {
        path: 'planAnalysis',
        name: `${PREFIX_SQL_SERVER}Plan Analysis`,
        component: () => import('@/sqlServer/views/planAnalysis/PlanAnalysis.vue'),
        beforeEnter: [requireInstances],
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
      {
        path: 'searchSql',
        name: `${PREFIX_SQL_SERVER}Search SQL`,
        component: () => import('@/sqlServer/views/searchSql/SearchSqlView.vue'),
        beforeEnter: [requireInstances],
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
    ],
    beforeEnter: [requireAuth, () => requireDomainTargetOrTutorial('database')],
    meta: {
      rolePermission: {
        [VIEW_MODE.EXEMONE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_SQLSERVER_PA,
        [VIEW_MODE.MAXGAUGE]:
          ROLE_PERMISSION_KEY.MONITORING.MONITORING_SQLSERVER_PERFORMANCE_ANALYSIS,
      },
      invisibleViewMode: [VIEW_MODE.INTERMAX],
      viewport: VIEWPORT_TYPE.PA,
    },
  },
  {
    path: '/sqlserver/',
    name: `${PREFIX_SQL_SERVER}Session Analysis`,
    component: BaseViewport,
    children: [
      {
        path: 'searchSession',
        name: `${PREFIX_SQL_SERVER}Search Session`,
        component: () => import('@/database/views/searchSession/sqlserver/SearchSession.vue'),
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
    ],
    beforeEnter: [requireAuth, () => requireDomainTargetOrTutorial('database'), requireInstances],
    meta: {
      rolePermission: {
        [VIEW_MODE.EXEMONE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_SQLSERVER_PA,
        [VIEW_MODE.MAXGAUGE]:
          ROLE_PERMISSION_KEY.MONITORING.MONITORING_SQLSERVER_PERFORMANCE_ANALYSIS,
      },
      invisibleViewMode: [VIEW_MODE.INTERMAX],
      viewport: VIEWPORT_TYPE.PA,
    },
  },
  {
    path: '/sqlserver/',
    name: `${PREFIX_SQL_SERVER}Performance Analysis`,
    component: BaseViewport,
    children: [
      {
        path: 'trendAnalysis',
        name: `${PREFIX_SQL_SERVER}Trend Analysis`,
        component: () => import('@/database/views/trendAnalysis/sqlserver/TrendAnalysis.vue'),
        beforeEnter: [requireInstances],
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
      {
        path: 'parameterHistory',
        name: `${PREFIX_SQL_SERVER}Parameter History`,
        component: () => import('@/database/views/parameterHistory/sqlserver/ParameterHistory.vue'),
        beforeEnter: [requireInstances],
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
      {
        path: 'alertHistory',
        name: `${PREFIX_SQL_SERVER}Alert History`,
        component: () => import('@/sqlServer/views/alertHistory/AlertHistory.vue'),
        beforeEnter: [requireInstances],
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
    ],
    beforeEnter: [requireAuth, () => requireDomainTargetOrTutorial('database')],
    meta: {
      rolePermission: {
        [VIEW_MODE.EXEMONE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_SQLSERVER_PA,
        [VIEW_MODE.MAXGAUGE]:
          ROLE_PERMISSION_KEY.MONITORING.MONITORING_SQLSERVER_PERFORMANCE_ANALYSIS,
      },
      invisibleViewMode: [VIEW_MODE.INTERMAX],
      viewport: VIEWPORT_TYPE.PA,
    },
  },
  {
    path: '/sqlserver/',
    name: `${PREFIX_SQL_SERVER}DataSpace Analysis`,
    component: BaseViewport,
    children: [
      {
        path: 'dbInfo',
        name: `${PREFIX_SQL_SERVER}DB Info`,
        component: () => import('@/sqlServer/views/dbInfo/DbInfo.vue'),
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
    ],
    beforeEnter: [requireAuth, () => requireDomainTargetOrTutorial('database'), requireInstances],
    meta: {
      rolePermission: {
        [VIEW_MODE.EXEMONE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_SQLSERVER_PA,
        [VIEW_MODE.MAXGAUGE]:
          ROLE_PERMISSION_KEY.MONITORING.MONITORING_SQLSERVER_PERFORMANCE_ANALYSIS,
      },
      invisibleViewMode: [VIEW_MODE.INTERMAX],
      viewport: VIEWPORT_TYPE.PA,
    },
  },
];

const subMenuMappingSqlServer = {
  instanceView: `${PREFIX_SQL_SERVER}Real Time Monitoring`,
  sqlAnalysis: `${PREFIX_SQL_SERVER}SQL Analysis`,
  sessionAnalysis: `${PREFIX_SQL_SERVER}Session Analysis`,
  performanceAnalysis: `${PREFIX_SQL_SERVER}Performance Analysis`,
  dataSpaceAnalysis: `${PREFIX_SQL_SERVER}DataSpace Analysis`,
};

const getSqlServerNaviMenu = (subMenu?: MaxgaugeDatabaseSubMenu['sqlserver']) => {
  const filteredSqlServerMenu = extractAccessibleRoutes(sqlServerMenu);

  if (subMenu && subMenuMappingSqlServer[subMenu]) {
    return filteredSqlServerMenu.filter((menu) => menu.name === subMenuMappingSqlServer[subMenu]);
  }

  return filteredSqlServerMenu.filter((menu) => menu.name !== subMenuMappingSqlServer.instanceView);
};

export { sqlServerMenu, getSqlServerNaviMenu, PREFIX_SQL_SERVER };
